import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { buildCanonicalPublicPath } from '@crm/shared/public-marketplace';
import {
  Check,
  CheckCircle2,
  CreditCard,
  FileText,
  Funnel,
  LayoutDashboard,
  Link as LinkIcon,
  Search,
  Shield,
  TrendingUp,
  Zap,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  DemoRequestForm,
  type DemoRequestPlanCode,
} from '../components/forms/DemoRequestForm';
import { ProductLedgerPreview } from '../components/home/ProductLedgerPreview';
import { StepProductMockup } from '../components/home/StepProductMockup';
import { ComplianceBadges } from '../components/marketing/ComplianceBadges';
import { HomeButton, homeButtonClasses } from '../components/home/HomeButton';
import { HomeSectionHeader } from '../components/home/HomeSectionHeader';
import { HOME_SURFACES } from '../components/home/homeSurfaces';
import {
  commercialPlans,
  getPlanPriceUsd,
  isPaidPlanCode,
  realEstatePricingSchema,
  type CommercialPlan,
  type BillingCadence,
} from '../config/commercialPlans';
import { commercialProducts } from '../config/commercialProducts';
import {
  MARKETPLACE_SITE_URL,
  segmentMarketingRoutes,
} from '../config/marketingRoutes';
import {
  defaultSeatBand,
  isEnterpriseSeatBand,
  type SeatBandCode,
} from '../config/seatBands';
import { EnterprisePriceLabel } from '../components/EnterprisePriceLabel';
import { PricingMarketingHints } from '../components/PricingMarketingHints';
import { PricingBillingToggle } from '../components/pricing/PricingBillingToggle';
import { PricingSeatBandToggle } from '../components/pricing/PricingSeatBandToggle';
import { trackMarketingEvent, useSectionViewTracking } from '../lib/analytics';
import { resolveCrmRegisterUrl } from '../lib/appUrls';
import { UsdPrice } from '../components/UsdPrice';
import { scrollToDemoForm } from '../lib/navigation';
import { cn } from '../lib/utils';
import { buildSiteUrl, usePageMetadata, useStructuredData } from '../lib/seo';

const PUBLIC_MARKETPLACE_HOME_PATH = buildCanonicalPublicPath('peru');
const PUBLIC_MARKETPLACE_SEARCH_TARGET = `${MARKETPLACE_SITE_URL}${PUBLIC_MARKETPLACE_HOME_PATH}?search={search_term_string}`;
const HOME_ORGANIZATION_ID = `${buildSiteUrl('/')}#organization`;
const HOME_WEBSITE_ID = `${buildSiteUrl('/')}#website`;
const HOME_SOFTWARE_ID = `${buildSiteUrl('/')}#crm-application`;
const HOME_CANONICAL_URL = buildSiteUrl('/');
const KICKER_CLASS = 'text-xs font-bold uppercase tracking-[0.14em]';
const PROBLEM_PAIR_KEYS = ['a', 'b', 'c'] as const;
const INTEGRATION_NAMES = [
  'WhatsApp',
  'Gmail',
  'OpenAI',
  'Anthropic',
  'Google AI',
  'Outlook',
] as const;

export function HomePage(): React.JSX.Element {
  const { t } = useTranslation([
    'home',
    'common',
    'pricing',
    'modules',
    'ai',
    'proof',
    'segments',
  ]);
  const [activeBand, setActiveBand] = useState<SeatBandCode>(defaultSeatBand);
  const [billingCadence, setBillingCadence] =
    useState<BillingCadence>('monthly');
  // Plan chosen on a pricing card; the demo form opens with it selected.
  const [demoPlanInterest, setDemoPlanInterest] = useState<
    DemoRequestPlanCode | undefined
  >(undefined);
  const crmRegisterUrl = resolveCrmRegisterUrl(
    import.meta.env.VITE_CRM_REGISTER_URL,
    import.meta.env.VITE_CRM_LOGIN_URL
  );

  usePageMetadata({
    title: t('home:meta.title'),
    description: t('home:meta.description'),
    path: '/',
  });

  const schema = useMemo<Record<string, unknown>>(
    () => ({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          '@id': HOME_ORGANIZATION_ID,
          name: 'Inmueble.red',
          url: HOME_CANONICAL_URL,
        },
        {
          '@type': 'WebSite',
          '@id': HOME_WEBSITE_ID,
          name: 'Inmueble.red',
          url: HOME_CANONICAL_URL,
          description: t('home:meta.description'),
          publisher: {
            '@id': HOME_ORGANIZATION_ID,
          },
          potentialAction: {
            '@type': 'SearchAction',
            target: PUBLIC_MARKETPLACE_SEARCH_TARGET,
            'query-input': 'required name=search_term_string',
          },
        },
        {
          '@type': 'SoftwareApplication',
          '@id': HOME_SOFTWARE_ID,
          name: 'Inmueble.red CRM',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Cloud',
          url: HOME_CANONICAL_URL,
          description: t('home:meta.description'),
          offers: {
            '@type': 'Offer',
            name: t('pricing:plans.free.name'),
            price: '0',
            priceCurrency: 'USD',
            availability: 'https://schema.org/InStock',
            url: crmRegisterUrl,
          },
        },
      ],
    }),
    [crmRegisterUrl, t]
  );

  useStructuredData({
    id: 'home-software',
    data: schema,
  });

  const productNameByCode = useMemo(() => {
    return new Map(
      commercialProducts.map((product) => [product.code, t(product.nameKey)])
    );
  }, [t]);
  const homeSeatBandLabels: Record<SeatBandCode, string> = {
    '1-3': t('pricing:seatBands.1-3'),
    '4-49': t('pricing:seatBands.4-49'),
    '50+': t('pricing:seatBands.50+'),
  };
  const isEnterpriseBand = isEnterpriseSeatBand(activeBand);
  const homeBillingLabels: Record<BillingCadence, string> = {
    monthly: t('pricing:billing.monthly'),
    annual: t('pricing:billing.annual'),
  };
  const homePriceUnitLabels: Record<BillingCadence, string> = {
    monthly: t('pricing:priceUnitMonthly'),
    annual: t('pricing:priceUnitAnnual'),
  };
  const billingNote =
    billingCadence === 'annual'
      ? t('pricing:billing.annualNote', {
          count: realEstatePricingSchema.annualBillingMonths,
          operate: realEstatePricingSchema.monthlyBillingMonths,
        })
      : t('pricing:billing.monthlyNote', {
          freeMonths: realEstatePricingSchema.annualFreeMonths,
        });
  const priceUnitLabel = homePriceUnitLabels[billingCadence];
  const heroRef = useSectionViewTracking('home_hero');
  const financeFlowRef = useSectionViewTracking('home_finance_flow');
  const complianceRef = useSectionViewTracking('home_compliance');
  const aiPreviewRef = useSectionViewTracking('home_ai_preview');
  const pricingPreviewRef = useSectionViewTracking('home_pricing_preview');
  const contactRef = useSectionViewTracking('home_contact');

  const heroCards = [
    {
      icon: Shield,
      title: t('home:homePage.heroCards.connect.title'),
      body: t('home:homePage.heroCards.connect.body'),
    },
    {
      icon: FileText,
      title: t('home:homePage.heroCards.payments.title'),
      body: t('home:homePage.heroCards.payments.body'),
    },
    {
      icon: LinkIcon,
      title: t('home:homePage.heroCards.traceability.title'),
      body: t('home:homePage.heroCards.traceability.body'),
    },
  ];
  const flowSteps = [
    {
      step: 'sales' as const,
      title: t('home:homePage.financeFlow.items.sales.title'),
      description: t('home:homePage.financeFlow.items.sales.description'),
    },
    {
      step: 'financing' as const,
      title: t('home:financeFlow.items.financing.title'),
      description: t('home:homePage.financeFlow.items.financing.description'),
    },
    {
      step: 'collections' as const,
      title: t('home:homePage.financeFlow.items.collections.title'),
      description: t('home:homePage.financeFlow.items.collections.description'),
    },
  ];
  const roleCards = [
    {
      icon: TrendingUp,
      title: t('home:homePage.roleSection.items.salesAgents.title'),
      description: t('home:homePage.roleSection.items.salesAgents.description'),
    },
    {
      icon: CreditCard,
      title: t('home:roleSection.items.collections.title'),
      description: t('home:roleSection.items.collections.description'),
    },
    {
      icon: LayoutDashboard,
      title: t('home:roleSection.items.management.title'),
      description: t('home:homePage.roleSection.items.management.description'),
    },
  ];
  const aiCards = [
    {
      icon: Zap,
      proof: t('ai:capabilities.collections_recommendation.proof'),
      title: t('ai:capabilities.collections_recommendation.title'),
      description: t('ai:capabilities.collections_recommendation.description'),
      impact: t('ai:capabilities.collections_recommendation.impact'),
    },
    {
      icon: Search,
      proof: t('ai:capabilities.voucher_ocr.proof'),
      title: t('ai:capabilities.voucher_ocr.title'),
      description: t('ai:capabilities.voucher_ocr.description'),
      impact: t('ai:capabilities.voucher_ocr.impact'),
    },
    {
      icon: Funnel,
      proof: t('ai:capabilities.smart_filter.proof'),
      title: t('ai:capabilities.smart_filter.title'),
      description: t('ai:capabilities.smart_filter.description'),
      impact: t('ai:capabilities.smart_filter.impact'),
    },
  ];
  const controlCards = [
    {
      label: t('proof:compliance.standards.lpdp'),
      title: t('proof:compliance.controls.lpdp_handling'),
      detail: t('proof:compliance.proofs.lpdp_handling'),
    },
    {
      label: t('home:homePage.controls.standards.access'),
      title: t('home:homePage.controls.cards.access.desc'),
      detail: t('home:homePage.controls.cards.access.detail'),
    },
    {
      label: t('proof:compliance.standards.encryption'),
      title: t('home:homePage.controls.cards.encryption.desc'),
      detail: t('proof:compliance.proofs.crypto_controls'),
    },
    {
      label: t('home:homePage.controls.standards.traceability'),
      title: t('home:homePage.controls.cards.traceability.desc'),
      detail: t('home:homePage.controls.cards.traceability.detail'),
    },
  ];

  return (
    <div className="home-landing flex flex-col">
      <section ref={heroRef} className="home-hero">
        <div className="section-padding relative z-10">
          <div className="grid grid-cols-1 items-center gap-10 xl:grid-cols-[minmax(0,1fr)_460px] xl:gap-16">
            <div className="max-w-3xl">
              <span
                className={cn(
                  KICKER_CLASS,
                  'mb-5 inline-block rounded-full border border-[var(--ir-terra)]/30 bg-[var(--ir-parchment)]/80 px-4 py-1.5 text-[var(--ir-terra-ink)] md:mb-8'
                )}
              >
                {t('home:hero.badge')}
              </span>
              <h1 className="mb-6 max-w-2xl font-serif text-5xl font-normal leading-[0.95] tracking-tight text-[var(--ir-ink)] md:text-6xl xl:text-[4.15rem]">
                {t('home:hero.title')}{' '}
                <span className="text-[var(--ir-terra)] italic">
                  {t('home:hero.titleHighlight')}
                </span>
              </h1>
              <p className="mb-8 max-w-2xl font-sans text-lg leading-relaxed text-[var(--ir-muted)] md:text-xl xl:text-2xl">
                {t('home:hero.subtitle')}
              </p>

              <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:gap-4">
                <a
                  href={crmRegisterUrl}
                  className={homeButtonClasses('accent', 'lg')}
                  onClick={() =>
                    trackMarketingEvent('hero_cta_click', {
                      location: 'home_hero',
                      cta: 'free_company_register',
                    })
                  }
                >
                  {t('common:cta.startFree')}
                </a>
                <HomeButton
                  type="button"
                  variant="outline"
                  size="lg"
                  onClick={() => {
                    trackMarketingEvent('hero_cta_click', {
                      location: 'home_hero',
                      cta: 'request_demo',
                    });
                    scrollToDemoForm();
                  }}
                >
                  {t('common:cta.requestDemo')}
                </HomeButton>
              </div>
              <p className="mt-6 max-w-2xl text-xs leading-relaxed text-[var(--ir-muted)]">
                {t('common:brand.cryptologyNote')}
              </p>
            </div>

            <ProductLedgerPreview />
          </div>

          <div className="mt-12 grid grid-cols-1 gap-3 md:mt-16 md:grid-cols-3">
            {heroCards.map((card) => (
              <div key={card.title} className="home-card flex gap-4 p-5">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[var(--ir-line)] bg-[var(--ir-ivory)]">
                  <card.icon
                    className="text-[var(--ir-terra)]"
                    size={20}
                    aria-hidden="true"
                  />
                </div>
                <div>
                  <h2 className="mb-1 text-lg font-bold text-[var(--ir-ink)]">
                    {card.title}
                  </h2>
                  <p className="text-sm text-[var(--ir-muted)]">{card.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className={HOME_SURFACES.ivory}>
        <div className="section-padding">
          <HomeSectionHeader
            badge={t('segments:hub.badge')}
            title={t('segments:hub.title')}
            subtitle={t('segments:hub.subtitle')}
          />
          <div className="grid grid-cols-1 gap-px overflow-hidden rounded-[1.75rem] border border-brand-primary/10 bg-brand-primary/10 md:grid-cols-2 xl:grid-cols-4">
            {segmentMarketingRoutes.map((route, index) => (
              <Link
                key={route.segmentCode}
                to={route.path}
                className="group flex min-h-[260px] flex-col justify-between bg-white p-8 transition-colors hover:bg-[var(--ir-ivory)] focus-visible:-outline-offset-4"
              >
                <div>
                  <span
                    className="font-mono text-xs text-[var(--ir-terra-ink)]"
                    aria-hidden="true"
                  >
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <h3 className="mt-6 font-serif text-3xl font-bold leading-tight text-brand-primary">
                    {t(`pricing:plans.${route.planCode}.name`)}
                  </h3>
                  <p className="mt-4 text-sm leading-relaxed text-[var(--ir-muted)]">
                    {t(`pricing:plans.${route.planCode}.description`)}
                  </p>
                </div>
                <span
                  className={cn(
                    KICKER_CLASS,
                    'mt-8 text-[var(--ir-terra-ink)] group-hover:text-brand-primary'
                  )}
                >
                  {t(route.primaryCtaKey)}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className={HOME_SURFACES.parchment}>
        <div className="section-padding">
          <HomeSectionHeader
            badge={t('home:problemSection.eyebrow')}
            title={t('home:problemSection.title')}
            subtitle={t('home:problemSection.subtitle')}
          />

          <div className="home-card overflow-hidden">
            <div
              className="hidden grid-cols-2 border-b border-[var(--ir-line)] md:grid"
              aria-hidden="true"
            >
              <p
                className={cn(
                  KICKER_CLASS,
                  'bg-[var(--ir-ivory)] px-10 py-6 text-[var(--ir-muted)]'
                )}
              >
                {t('home:homePage.problem.beforeHeading')}
              </p>
              <p
                className={cn(
                  KICKER_CLASS,
                  'border-l border-[var(--ir-line)] bg-white px-10 py-6 text-[var(--ir-forest)]'
                )}
              >
                {t('home:problemSection.afterLabel')}
              </p>
            </div>
            <ul>
              {PROBLEM_PAIR_KEYS.map((pairKey, index) => (
                <li
                  key={pairKey}
                  className={cn(
                    'grid grid-cols-1 md:grid-cols-2',
                    index > 0 && 'border-t border-[var(--ir-line)]'
                  )}
                >
                  <div className="flex gap-5 bg-[var(--ir-ivory)] p-8 md:p-10">
                    <span
                      className="font-serif text-2xl italic text-[var(--ir-muted)]"
                      aria-hidden="true"
                    >
                      {pairKey.toUpperCase()}.
                    </span>
                    <p className="text-[var(--ir-muted)]">
                      <span
                        className={cn(
                          KICKER_CLASS,
                          'mb-2 block text-[var(--ir-muted)] md:sr-only'
                        )}
                      >
                        {t('home:homePage.problem.beforeHeading')}
                      </span>
                      {t(`home:homePage.problem.before.${pairKey}`)}
                    </p>
                  </div>
                  <div className="flex gap-5 border-t border-[var(--ir-line)] bg-white p-8 md:border-l md:border-t-0 md:p-10">
                    <CheckCircle2
                      className="shrink-0 text-[var(--ir-forest)]"
                      size={28}
                      aria-hidden="true"
                    />
                    <p className="text-lg font-medium text-[var(--ir-ink)]">
                      <span
                        className={cn(
                          KICKER_CLASS,
                          'mb-2 block text-[var(--ir-forest)] md:sr-only'
                        )}
                      >
                        {t('home:problemSection.afterLabel')}
                      </span>
                      {t(`home:homePage.problem.after.${pairKey}`)}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section
        ref={financeFlowRef}
        id="how-it-works"
        className={HOME_SURFACES.ivory}
      >
        <div className="section-padding">
          <HomeSectionHeader
            badge={t('home:financeFlow.eyebrow')}
            title={t('home:financeFlow.title')}
            subtitle={t('home:financeFlow.subtitle')}
          />

          <div className="space-y-14">
            {flowSteps.map((flowStep, index) => (
              <div
                key={flowStep.step}
                className={cn(
                  'flex flex-col items-center gap-8 md:gap-16',
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                )}
              >
                <div className="w-full md:w-1/2">
                  <div className="mb-6 flex items-center gap-5">
                    <div
                      className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-[var(--ir-terra)]/30 bg-[var(--ir-parchment)] font-serif text-lg italic text-[var(--ir-terra-ink)]"
                      aria-hidden="true"
                    >
                      {String(index + 1).padStart(2, '0')}
                    </div>
                    <h3 className="text-3xl font-bold tracking-tight">
                      {flowStep.title}
                    </h3>
                  </div>
                  <p className="text-lg leading-relaxed text-[var(--ir-muted)] md:text-xl">
                    {flowStep.description}
                  </p>
                </div>
                <div className="w-full md:w-1/2">
                  <StepProductMockup step={flowStep.step} />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-20 border-t border-[var(--ir-line)] pt-14">
            <h3 className="font-serif text-3xl font-normal leading-tight tracking-tight text-[var(--ir-ink)] md:text-4xl">
              {t('home:roleSection.title')}
            </h3>
            <p className="mt-4 max-w-2xl text-lg leading-relaxed text-[var(--ir-muted)]">
              {t('home:roleSection.subtitle')}
            </p>
            <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
              {roleCards.map((roleCard) => (
                <div key={roleCard.title} className="home-card p-8">
                  <div className="mb-6 flex h-10 w-10 items-center justify-center rounded-full border border-[var(--ir-line)] text-[var(--ir-forest)]">
                    <roleCard.icon size={22} aria-hidden="true" />
                  </div>
                  <h4 className="mb-3 font-serif text-2xl font-bold">
                    {roleCard.title}
                  </h4>
                  <p className="leading-relaxed text-[var(--ir-muted)]">
                    {roleCard.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section ref={aiPreviewRef} id="ia" className={HOME_SURFACES.parchment}>
        <div className="section-padding">
          <HomeSectionHeader
            badge={t('home:aiProof.eyebrow')}
            title={t('home:aiProof.title')}
            subtitle={t('home:aiProof.subtitle')}
          />

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
            {aiCards.map((aiCard) => (
              <article
                key={aiCard.title}
                className="home-card flex flex-col p-8"
              >
                <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-full border border-brand-teal/20 text-brand-teal">
                  <aiCard.icon size={24} aria-hidden="true" />
                </div>
                <p className={cn(KICKER_CLASS, 'mb-3 text-[var(--ir-muted)]')}>
                  {aiCard.proof}
                </p>
                <h3 className="mb-3 text-2xl font-bold leading-tight">
                  {aiCard.title}
                </h3>
                <p className="mb-6 flex-grow leading-relaxed text-[var(--ir-muted)]">
                  {aiCard.description}
                </p>
                <p className="text-sm font-bold text-brand-teal">
                  {aiCard.impact}
                </p>
              </article>
            ))}
          </div>

          <div className="mt-14 border-t border-brand-primary/10 pt-10">
            <p
              className={cn(
                KICKER_CLASS,
                'mb-6 text-center text-[var(--ir-muted)]'
              )}
            >
              {t('proof:integrations.compactTitle')}
            </p>
            <ul className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
              {INTEGRATION_NAMES.map((integration) => (
                <li
                  key={integration}
                  className="rounded-full border border-brand-primary/10 bg-brand-bg px-4 py-3 text-center text-sm font-bold text-brand-primary"
                >
                  {integration}
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-10 flex justify-center">
            <Link
              to="/ia"
              className={homeButtonClasses('outline', 'md')}
              onClick={() =>
                trackMarketingEvent('hero_cta_click', {
                  location: 'home_ai_proof',
                  cta: 'view_ai',
                })
              }
            >
              {t('home:aiProof.viewAll')}
            </Link>
          </div>
        </div>
      </section>

      <section ref={complianceRef} className={HOME_SURFACES.deep}>
        <div className="section-padding relative z-10">
          <HomeSectionHeader
            badge={t('home:homePage.controls.badge')}
            light
            title={t('proof:compliance.title')}
            subtitle={t('proof:compliance.subtitle')}
          />

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {controlCards.map((controlCard) => (
              <div
                key={controlCard.label}
                className="rounded-[1.5rem] border border-white/10 bg-white/[0.04] p-8 transition-colors hover:bg-white/[0.07]"
              >
                <p className={cn(KICKER_CLASS, 'mb-5 text-[var(--ir-clay)]')}>
                  {controlCard.label}
                </p>
                <h3 className="mb-4 text-2xl font-bold leading-tight">
                  {controlCard.title}
                </h3>
                <p className="leading-relaxed text-white/70">
                  {controlCard.detail}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-10">
            <HomeButton
              type="button"
              variant="accent"
              size="lg"
              onClick={() => {
                trackMarketingEvent('trust_band_cta_click', {
                  location: 'home_trust_band',
                  cta: 'request_demo',
                });
                scrollToDemoForm();
              }}
            >
              {t('common:cta.requestDemo')}
            </HomeButton>
          </div>
        </div>
      </section>

      <section
        ref={pricingPreviewRef}
        id="precios"
        className={HOME_SURFACES.ivory}
      >
        <div className="section-padding">
          <HomeSectionHeader
            badge={t('home:homePage.pricing.badge')}
            title={t('home:homePage.pricing.title')}
            subtitle={t('home:homePage.pricing.subtitle')}
          />

          <div className="mb-10 grid grid-cols-1 gap-6 lg:grid-cols-[1.7fr_1fr]">
            <div>
              <p
                id="home-pricing-band-label"
                className={cn(KICKER_CLASS, 'mb-3 text-[var(--ir-muted)]')}
              >
                {t('pricing:matrix.licenses')}
              </p>
              <PricingSeatBandToggle
                selectedBand={activeBand}
                onSelect={setActiveBand}
                labelForBand={(band) => homeSeatBandLabels[band]}
                ariaLabelledBy="home-pricing-band-label"
              />
              <PricingMarketingHints
                seatBand={activeBand}
                billingCadence={billingCadence}
                tone="home"
                className="mt-4"
              />
            </div>

            <PricingBillingToggle
              billingCadence={billingCadence}
              onSelect={setBillingCadence}
              title={t('pricing:billing.title')}
              monthlyLabel={homeBillingLabels.monthly}
              annualLabel={homeBillingLabels.annual}
              annualBadge={t('pricing:billing.annualBadge', {
                count: realEstatePricingSchema.annualFreeMonths,
              })}
            />
          </div>

          <div
            className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4"
            data-testid="home-pricing-plan-grid"
          >
            {commercialPlans.map((plan: CommercialPlan) => {
              const isPopular = plan.popular;
              const isSelfServicePlan = plan.ctaType === 'self_service';
              const paidPlanCode = isPaidPlanCode(plan.code) ? plan.code : null;
              const isPaidPlan = paidPlanCode !== null;
              const selectedPrice = paidPlanCode
                ? getPlanPriceUsd(paidPlanCode, activeBand, billingCadence)
                : null;
              const usesEnterprisePrice = isEnterpriseBand && isPaidPlan;
              if (
                isPaidPlan &&
                !usesEnterprisePrice &&
                selectedPrice === null
              ) {
                throw new Error(
                  `Missing pricing for plan ${plan.code} in ${activeBand} band`
                );
              }
              const planToneClasses = isPopular
                ? 'border-brand-primary/10 bg-brand-primary text-white shadow-[0_26px_80px_-50px_rgba(0,31,34,0.78)]'
                : isSelfServicePlan
                  ? 'border-brand-teal/30 bg-white text-brand-primary shadow-[0_24px_70px_-56px_rgba(0,145,112,0.45)]'
                  : 'border-brand-primary/10 bg-white text-brand-primary shadow-[0_20px_60px_-55px_rgba(0,31,34,0.65)]';
              const mutedTextClass = isPopular
                ? 'text-white/75'
                : 'text-[var(--ir-muted)]';
              const checkClass = isPopular
                ? 'text-[var(--ir-clay)]'
                : 'text-brand-teal';

              // Each card spans the same six row tracks, so names, prices and
              // buttons line up across plans whatever the copy length.
              return (
                <article
                  key={plan.code}
                  className={cn(
                    'relative row-span-6 grid grid-rows-subgrid gap-y-0 rounded-[1.5rem] border p-8',
                    planToneClasses
                  )}
                  data-testid={`home-pricing-plan-${plan.code}`}
                >
                  <div className="mb-5 grid min-h-6 grid-cols-2 gap-2">
                    <div
                      className="flex justify-start"
                      data-testid="recommendation-badge-slot"
                    >
                      {isPopular ? (
                        <div className="whitespace-nowrap rounded-full bg-brand-accent px-3 py-1 text-xs font-bold uppercase tracking-[0.08em] text-white">
                          {t('pricing:plans.popular')}
                        </div>
                      ) : null}
                    </div>
                    <div
                      className="flex justify-end"
                      data-testid="self-service-badge-slot"
                    >
                      {isSelfServicePlan ? (
                        <div className="whitespace-nowrap rounded-full bg-brand-teal px-3 py-1 text-xs font-bold uppercase tracking-[0.08em] text-white">
                          {t('common:cta.freePlanSignup')}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <h3 className="mb-4 font-serif text-3xl font-bold leading-tight">
                    {t(plan.nameKey)}
                  </h3>
                  <p className={cn('mb-8 leading-relaxed', mutedTextClass)}>
                    {t(plan.descriptionKey)}
                  </p>

                  <div className="mb-8">
                    <span
                      className={cn(
                        'mb-2 block text-xs uppercase tracking-[0.12em]',
                        mutedTextClass
                      )}
                    >
                      {isSelfServicePlan
                        ? t('pricing:freePriceCaption')
                        : t('pricing:priceCaption')}
                    </span>
                    {isSelfServicePlan ? (
                      <>
                        <div className="flex items-baseline gap-2">
                          <UsdPrice
                            value={
                              realEstatePricingSchema.freeTerms.monthlyPriceUsd
                            }
                            className="font-serif text-5xl tracking-tighter"
                          />
                          <span className="font-medium text-[var(--ir-muted)]">
                            {t('pricing:freePriceUnit')}
                          </span>
                        </div>
                        <p className="mt-4 text-sm leading-relaxed text-[var(--ir-muted)]">
                          {t('pricing:freePriceNote', {
                            individualUsers:
                              realEstatePricingSchema.freeTerms
                                .individualActiveUsers,
                            businessUsers:
                              realEstatePricingSchema.freeTerms
                                .businessActiveUsers,
                          })}
                        </p>
                      </>
                    ) : usesEnterprisePrice ? (
                      <EnterprisePriceLabel
                        variant={isPopular ? 'onDark' : 'default'}
                      />
                    ) : (
                      <>
                        <div className="flex items-baseline gap-2">
                          <UsdPrice
                            value={selectedPrice as number}
                            variant={isPopular ? 'onDark' : 'default'}
                            className={cn(
                              'font-serif text-5xl tracking-tighter',
                              isPopular && 'text-white'
                            )}
                          />
                          <span className={cn('font-medium', mutedTextClass)}>
                            {priceUnitLabel}
                          </span>
                        </div>
                        <p
                          className={cn(
                            'mt-4 text-sm leading-relaxed',
                            mutedTextClass
                          )}
                        >
                          {billingNote}
                        </p>
                      </>
                    )}
                  </div>

                  <ul className="mb-8 space-y-4">
                    {plan.includesModules.map((moduleCode) => (
                      <li
                        key={moduleCode}
                        className={cn(
                          'flex items-start gap-4 font-medium',
                          isPopular ? 'text-white/90' : 'text-brand-primary'
                        )}
                      >
                        <Check
                          size={20}
                          className={cn('mt-0.5 shrink-0', checkClass)}
                          aria-hidden="true"
                        />
                        <span>
                          {productNameByCode.get(moduleCode) ?? moduleCode}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {isSelfServicePlan ? (
                    <a
                      href={crmRegisterUrl}
                      className={cn(
                        homeButtonClasses('accent', 'md'),
                        'w-full justify-center self-end whitespace-nowrap'
                      )}
                      onClick={() =>
                        trackMarketingEvent('pricing_plan_select', {
                          planCode: plan.code,
                          seatBand: activeBand,
                          location: 'home_pricing_plan_card',
                        })
                      }
                    >
                      {t('common:cta.startFree')}
                    </a>
                  ) : (
                    <HomeButton
                      type="button"
                      variant={isPopular ? 'accent' : 'secondary'}
                      className="w-full self-end whitespace-nowrap"
                      onClick={() => {
                        trackMarketingEvent('pricing_plan_select', {
                          planCode: plan.code,
                          seatBand: activeBand,
                          location: 'home_pricing_plan_card',
                        });
                        if (paidPlanCode) {
                          setDemoPlanInterest(paidPlanCode);
                        }
                        scrollToDemoForm();
                      }}
                    >
                      {t('common:cta.requestDemo')}
                    </HomeButton>
                  )}
                </article>
              );
            })}
          </div>

          <p className="mx-auto mt-8 max-w-3xl text-center text-xs leading-relaxed text-[var(--ir-muted)]">
            {t('home:homePage.pricing.disclaimer')}
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
            <Link to="/precios" className={homeButtonClasses('outline', 'md')}>
              {t('home:homePage.pricing.comparePlans')}
            </Link>
            <Link to="/modulos" className={homeButtonClasses('ghost', 'md')}>
              {t('home:homePage.pricing.viewModules')}
            </Link>
          </div>
        </div>
      </section>

      <section ref={contactRef} className={HOME_SURFACES.parchment}>
        <div className="section-padding">
          <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
            <div>
              <HomeSectionHeader
                badge={t('home:homePage.contact.badge')}
                title={t('home:cta.title')}
                subtitle={t('home:cta.subtitle')}
              />
              <ul className="space-y-6">
                {[t('home:cta.supporting'), t('home:cta.expectation')].map(
                  (point) => (
                    <li key={point} className="flex gap-4">
                      <Check
                        size={22}
                        className="mt-1 shrink-0 text-brand-teal"
                        aria-hidden="true"
                      />
                      <p className="text-lg text-[var(--ir-muted)]">{point}</p>
                    </li>
                  )
                )}
              </ul>

              <ComplianceBadges className="mt-12" />
            </div>

            <div
              id="demo-form"
              className="scroll-mt-24 rounded-[1.75rem] border border-brand-primary/10 bg-white p-8 shadow-[0_24px_70px_-58px_rgba(0,31,34,0.7)] md:p-12"
            >
              <DemoRequestForm
                variant="full"
                appearance="underlined"
                submitLabel={t('common:cta.requestDemo')}
                helperText={t('home:homePage.contact.helperText')}
                defaultPlanInterest={demoPlanInterest}
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
